import { useState, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { People, Global, Verify } from "iconsax-react";
import { Badge } from "@/shared/ui";
import type { ProjectPartner } from "../../../types";

interface Props {
  partners: ProjectPartner[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}

export default function PartnersBadge({ partners, t }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uid = useId();
  const portalId = `partners-popover-${uid}`;

  function scheduleClose() {
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  }

  function cancelClose() {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function handleTriggerEnter() {
    cancelClose();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 296 - 8));
    setPos({ top: rect.bottom + 6, left });
    setOpen(true);
  }

  const popover = open
    ? createPortal(
        <div
          id={portalId}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 10200 }}
          className="w-72 radius-md border surface-base-stroke surface-base-fill elevation-level-2 p-3 flex flex-col gap-0.5"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}>
          <p className="text-body-bold-sm content-base-primary mb-1.5">{t("hero.partnersPopoverTitle")}</p>
          {partners.map((partner, i) => (
            <div key={i} className="flex items-center gap-1.5 py-0.5">
              <span className="content-action-brand">
                <Verify size={14} color="currentColor" />
              </span>
              <span className="text-body-regular-sm content-base-primary">{partner.partner_name}</span>
              {partner.is_external && (
                <span title={t("hero.partnerExternal")} className="shrink-0 content-action-info cursor-help">
                  <Global size={14} color="currentColor" />
                </span>
              )}
            </div>
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={containerRef} onMouseEnter={handleTriggerEnter} onMouseLeave={scheduleClose}>
      <Badge
        variant="soft"
        color="info"
        icon={<People size={16} color="currentColor" />}
        text={t("hero.partnersCount", { count: partners.length })}
        title=""
        clickable
      />
      {popover}
    </div>
  );
}

